import React, { FunctionComponent, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { toast } from 'react-toastify';
import Apis from '../apis';
import {
  EditableTable,
  HeaderDisplayKeysWithType,
  InlineEditableTable,
} from '../components';
import { ClassSem, WrapClassBaseLayout } from '../layouts';
import { SimpleStudent, PHITee } from '../model';
import { ErrorDispatches } from '../redux/Dispatches';
import { ApplicationState } from '../redux/States';
import { Nullable } from '../types';
import {
  PHITeeCalculators,
  TeeFollowUpTableHeader,
  TeeTableHeader,
} from './TableHeaders/TeeHeader';
import {
  DefaultTeeContext,
  ExtractedPHITee,
  combindFirstAndFollowUpExamination,
  extractFirstAndFollowUpExamination,
} from '../utils/tee';

type TeePageData = SimpleStudent & Nullable<ExtractedPHITee>;

const firstTeeHeader: HeaderDisplayKeysWithType<TeePageData>[] = [
  { property: 'seat', display: '座號' },
  { property: 'name', display: '學生' },
  ...(TeeTableHeader as HeaderDisplayKeysWithType<TeePageData>[]),
];

const followUpTeeHeader: HeaderDisplayKeysWithType<TeePageData>[] = [
  { property: 'seat', display: '座號' },
  { property: 'name', display: '學生' },
  ...(TeeFollowUpTableHeader as HeaderDisplayKeysWithType<TeePageData>[]),
];

const mapState = (state: ApplicationState, ownProps: ClassSem) => ({
  ...ownProps,
});
const mapDispatch = { ...ErrorDispatches };
const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;

const classPHITeePage: FunctionComponent<Props> = ({
  classId,
  year,
  sem,
  catchErrorForModal,
}) => {
  const [students, setStudent] = useState([] as TeePageData[]);
  const [followUp, setFollowUp] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [inputing, setInputing] = useState(false);
  const [teeFromPhysical, setTeeFromPhysical] = useState<boolean>(false);

  useEffect(() => {
    refreshStudents();
  }, [classId, year, sem, inputing, toggle]);

  function refreshStudents() {
    if (classId && year && sem) {
      // const rawSettings = await Apis.getPhysicalSettings(year, year);
      Apis.getClassStudentTee(classId, year, sem)
        .then((ss) => {
          if (ss) {
            const isFromPhysical = ss.filter((t) => t.teeFromPhysical);
            setTeeFromPhysical(isFromPhysical.length > 0);
          }
          setStudent(
            ss.map(({ tee, teeFromPhysical, ...s }) => ({
              ...s,
              sid: s.sid || '',
              ...(tee ? extractFirstAndFollowUpExamination(tee) : {}),
            }))
          );
        })
        .catch(catchErrorForModal);
    } else {
      setStudent([]);
    }
  }

  function onHide() {
    setInputing(false);
  }

  function onDelete(sight: TeePageData) {
    toast
      .promise(Apis.deleteStudentTee(sight.pid, year, sem), {
        pending: '口檢資料刪除中......',
        success: '口檢資料刪除成功！',
        error: '口檢資料刪除失敗！請查看錯誤資訊。',
      })
      .then(() => setToggle(!toggle))
      .catch(catchErrorForModal);
  }

  function onUpdateStudent(pid: string, tee: PHITee) {
    toast.dismiss();
    toast
      .promise(Apis.InsertStudentTee(pid, tee), {
        pending: '口檢資料上傳中......',
        success: '口檢資料上傳成功！',
        error: '口檢資料上傳失敗！請查看錯誤資訊。',
      })
      .then(() => {
        setToggle(!toggle);
      })
      .catch(catchErrorForModal);
  }
  return (
    <>
      <div className={teeFromPhysical ? 'text-danger' : 'd-none'}>
        同步自健檢資料，若有編輯需求請至學生健康資料-身體診察
      </div>

      <div
        className="mt-3 p-3"
        style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          fontSize: '15px',
          lineHeight: '1.6',
          margin:'10px -15px'
        }}
      >
        <div>
          <strong>口檢表使用提醒：</strong>
        </div>

        <div>
          1️⃣ 請注意，使用口檢表時，請不要封鎖快顯視窗。
        </div>

        <div>
          2️⃣ 如果出現 undefined 請用鍵盤刪除。
        </div>

        <div>
          3️⃣ 口檢表標記一律改為大寫，乳齒恆齒以象限判斷。
        </div>

        <div>
          4️⃣ UPFDE：上顎恆牙第一大臼齒齲齒經驗；DNFDE：下顎恆牙第一大臼齒齲齒經驗
        </div>

        <div>
          5️⃣ 提醒！學生完成齲齒複檢項目應在「未治療齲齒」欄位註記複診正常或複診異常。
        </div>
      </div>

      <ul className="nav nav-tabs" style={{ margin: '0 -15px' }}>
        <li>
          <summary
            className={`text-dark font-weight-bold wid-100 nav-link ${!followUp ? 'active' : ''
              }`}
            onClick={() => setFollowUp(false)}
          >
            初檢
          </summary>
        </li>
        <li>
          <summary
            className={`text-dark font-weight-bold wid-100 nav-link ${followUp ? 'active' : ''
              }`}
            onClick={() => setFollowUp(true)}
          >
            複檢
          </summary>
        </li>
      </ul>
      <div
        className="border-left border-right border-bottom p-1 py-2"
        style={{ margin: '0 -15px' }}
      >
        {teeFromPhysical ? (
          <>
            <EditableTable
              headers={followUp ? followUpTeeHeader : firstTeeHeader}
              values={students}
              fixCol={2}
            />
          </>
        ) : (
          <>
            <InlineEditableTable
              headers={followUp ? followUpTeeHeader : firstTeeHeader}
              values={students}
              fixCol={2}
              autoNext={true}
              decorators={PHITeeCalculators}
              onValueUpdate={(value: TeePageData) => {
                const data: PHITee = {
                  ...combindFirstAndFollowUpExamination(value),
                  id: 0,
                  sem,
                  year,
                  yearClassId: 0,
                };
                onUpdateStudent(value.pid, data);
              }}
              onValueDelete={onDelete}
              onRowRender={(v) => ({ deleteable: true, editable: true })}
              context={DefaultTeeContext}
            />
          </>
        )}
      </div>
    </>
  );
};

export const ClassPHITeePage = connector(WrapClassBaseLayout(classPHITeePage));
